import { create } from "zustand";
import { streamAssistantChat, type ChatContext, type ChatEvent, type ChatTurn, type Provenance } from "./assistantApi";

/** One tool call the model made while answering — shown as the work behind the reply. */
export interface Step { name: string; args: Record<string, unknown>; error?: string; done: boolean }

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  steps: Step[];
  provenances: Provenance[];
  truncated?: boolean;
  error?: string;
}

interface AssistantState {
  open: boolean;
  /** The site the transcript belongs to — a site change starts a fresh one. */
  siteId: string | null;
  messages: ChatMessage[];
  busy: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  clear: () => void;
  send: (siteId: string, text: string, context: ChatContext) => Promise<void>;
  /** Applies one stream event to the pending assistant message (exported for tests). */
  apply: (e: ChatEvent) => void;
}

let seq = 0;
const nextId = () => `m${Date.now().toString(36)}${(seq++).toString(36)}`;

export const useAssistantStore = create<AssistantState>((set, get) => ({
  open: false,
  siteId: null,
  messages: [],
  busy: false,
  setOpen(open) { set({ open }); },
  toggle() { set((s) => ({ open: !s.open })); },
  clear() { set({ messages: [] }); },

  apply(e) {
    set((s) => {
      const last = s.messages[s.messages.length - 1];
      if (!last || last.role !== "assistant") return s;
      const msg: ChatMessage = { ...last, steps: [...last.steps] };
      switch (e.event) {
        case "tool":
          msg.steps.push({ name: e.name, args: e.args ?? {}, done: false });
          break;
        case "answer": {
          // The first open step of that name is the one that just returned (calls run in order).
          const i = msg.steps.findIndex((st) => st.name === e.name && !st.done);
          if (i >= 0) msg.steps[i] = { ...msg.steps[i], done: true, error: e.error };
          break;
        }
        case "text":
          msg.text = e.text;
          msg.truncated = e.truncated;
          break;
        case "done":
          msg.provenances = e.provenances ?? [];
          break;
        case "error":
          msg.error = e.message;
          break;
      }
      return { messages: [...s.messages.slice(0, -1), msg] };
    });
  },

  async send(siteId, text, context) {
    const trimmed = text.trim();
    if (!trimmed || get().busy) return;
    const fresh = get().siteId !== siteId;
    const prior = fresh ? [] : get().messages;
    const history: ChatTurn[] = prior
      .filter((m) => m.text && !m.error)
      .map((m) => ({ role: m.role, content: m.text }));
    history.push({ role: "user", content: trimmed });
    set({
      siteId,
      busy: true,
      messages: [
        ...prior,
        { id: nextId(), role: "user", text: trimmed, steps: [], provenances: [] },
        { id: nextId(), role: "assistant", text: "", steps: [], provenances: [] },
      ],
    });
    try {
      await streamAssistantChat(siteId, history, context, (e) => get().apply(e));
    } catch (err) {
      get().apply({ event: "error", status: 0, message: err instanceof Error ? err.message : String(err) });
    } finally {
      set({ busy: false });
    }
  },
}));
